import { NextRequest, NextResponse } from 'next/server'
import { geolocation } from '@vercel/functions'

import { CAMPAIGN_PARTNER_PARAM, FUNNEL_PARTNER_KEYS } from '~/campaigns/consts/campaignPartnerKeys.const'
import appConfig from '~/config/app'
import {
  NO_ONBOARDING_EXPERIMENT_FLAG,
  OnboardingArmRoute,
  US_ONBOARDING_EXPERIMENT_FLAG,
} from '~/onboarding-flow/consts/experiment.const'

// Sibling of this file, not a `../` hop out of src: schoolPlatformRoutes lives in the root `utils/`
// dir because next.config.js has to require() it and cannot resolve the `~/` alias or TypeScript.
import { isRedirectedToHomeInSchoolPlatform } from './utils/schoolPlatformRoutes'

// Edge middleware runs before rewrites, so this is the one PostHog call that does not go through
// the `/ph` proxy — it reads the regional host straight from env.
const POSTHOG_FLAGS_URL = `${process.env.NEXT_PUBLIC_POSTHOG_API_HOST}/flags?v=2`
const HOMEPAGE_PATHNAME_REGEXP = /^\/(en|no|uk)?\/?$/
const PRICE_PAGE_PATHNAME_REGEXP = /^(\/(?:en|no|uk))?\/get-started\/price\/?$/
const GET_STARTED_INTRO_PAGE_PATHNAME_REGEXP = /^(\/(?:en|no|uk))?\/get-started\/intro\/?$/

const JULY_NO_LANDING_PAGE = '150726-no-landing-page'
const JULY_US_LANDING_PAGE = '150726-us-landing-page'

export const POSTHOG_BOOTSTRAP_COOKIE_KEY = 'bootstrapData'

interface PosthogBootstrapFlags {
  [key: string]: string | boolean
}

interface PosthogDetailedFlag {
  enabled?: boolean
  variant?: string
}

interface PosthogFlagsResponse {
  featureFlags?: PosthogBootstrapFlags
  flags?: Record<string, string | boolean | PosthogDetailedFlag>
}

export type PosthogSuperProperties = Record<string, string | boolean | number>

export interface PosthogBootstrapPayload {
  distinctID: string
  isIdentifiedID: boolean
  featureFlags: PosthogBootstrapFlags
  superProperties: PosthogSuperProperties
}

type LandingVariant = 'control' | 'variant'

const getLandingVariantFromFlags = (featureFlags: PosthogBootstrapFlags): LandingVariant => {
  const landingFlagValue = featureFlags[JULY_NO_LANDING_PAGE] || featureFlags[JULY_US_LANDING_PAGE]

  if (landingFlagValue === 'variant') {
    return 'variant'
  }

  return 'control'
}

// Only the exact string counts as taking part — a disabled flag is `false`, and anyone outside both
// tests gets the control, the Quijote funnel production already serves.
const getOnboardingArmRoute = (featureFlags: PosthogBootstrapFlags): OnboardingArmRoute =>
  (featureFlags[NO_ONBOARDING_EXPERIMENT_FLAG] || featureFlags[US_ONBOARDING_EXPERIMENT_FLAG]) === 'variant'
    ? 'variant'
    : 'control'

// Works for any pathname, e.g. '/en/get-started/price' → '/en', '/' → ''
const getLocalePrefix = (pathname: string): string => {
  const localeMatch = pathname.match(/^\/(en|no|uk)(?:\/|$)/)
  return localeMatch ? `/${localeMatch[1]}` : ''
}

// '/no/login' → '/login', '/login' → '/login', '/no' → '/'. Whether nextUrl.pathname still carries
// the locale here depends on how the request arrived, so both shapes have to normalize the same way
// before they are matched against the route lists.
const stripLocalePrefix = (pathname: string): string => pathname.slice(getLocalePrefix(pathname).length) || '/'

const normalizeFeatureFlags = (payload: PosthogFlagsResponse): PosthogBootstrapFlags => {
  if (payload.flags) {
    return Object.entries(payload.flags).reduce<PosthogBootstrapFlags>((acc, [flagKey, flagValue]) => {
      if (typeof flagValue === 'string' || typeof flagValue === 'boolean') {
        acc[flagKey] = flagValue
        return acc
      }

      if (typeof flagValue === 'object' && flagValue !== null) {
        if (typeof flagValue.variant === 'string') {
          acc[flagKey] = flagValue.variant
        } else if (typeof flagValue.enabled === 'boolean') {
          acc[flagKey] = flagValue.enabled
        }
      }

      return acc
    }, {})
  }

  return payload.featureFlags || {}
}

export default async function middleware(request: NextRequest): Promise<NextResponse> {
  // const maybeRedirectResponse = handleLocaleRedirects(request)
  // if (maybeRedirectResponse) return maybeRedirectResponse

  // The b2c auth pages (/login, /register) are disabled in the school edition and answered by the
  // role selection at `/` — see utils/schoolPlatformRoutes.js.
  //
  // This CANNOT be a `redirects()` entry in next.config.js. A config redirect is correct for a
  // document request, but on a client-side `router.push(ROUTES.LOGIN)` the pages router pushes
  // /login into history FIRST and only then fetches /_next/data/<id>/en/login.json. The config
  // redirect fires on that data request, the router quietly resolves `/` instead, and you end up on
  // the address bar's /login rendering the home page's components. A middleware redirect answers
  // with `x-nextjs-redirect`, which the router turns into a real navigation, so the URL and the
  // rendered page agree on both hops. Middleware also runs before beforeFiles rewrites, so this
  // wins over the /404 block for any path that ends up in both lists.
  if (appConfig.isSchoolPlatform && isRedirectedToHomeInSchoolPlatform(stripLocalePrefix(request.nextUrl.pathname))) {
    const homeUrl = request.nextUrl.clone()
    homeUrl.pathname = getLocalePrefix(request.nextUrl.pathname) || '/'
    // Drop `?from=/dashboard` and friends — role selection has no use for a b2c post-login target.
    homeUrl.search = ''

    return NextResponse.redirect(homeUrl)
  }

  const isHttps = request.nextUrl.protocol === 'https:' || request.headers.get('x-forwarded-proto') === 'https'
  // The school edition serves role selection at `/`. Without this the rewrite below hands the
  // school homepage back to b2c marketing and the gate in `pages/index.tsx` never runs. There is no
  // landing A/B test in that edition anyway, so the PostHog bootstrap is skipped with it.
  const isHomepage = !appConfig.isSchoolPlatform && HOMEPAGE_PATHNAME_REGEXP.test(request.nextUrl.pathname)
  const isPricePage = PRICE_PAGE_PATHNAME_REGEXP.test(request.nextUrl.pathname)
  const isIntroPage = GET_STARTED_INTRO_PAGE_PATHNAME_REGEXP.test(request.nextUrl.pathname)
  const posthogToken = process.env.NEXT_PUBLIC_POSTHOG_KEY

  const country = (geolocation(request) && geolocation(request).country) || ''
  const city = (geolocation(request) && geolocation(request).city) || ''

  // One id identifies this browser. posthog-js sets an anonymous user's $device_id equal to the
  // bootstrapped distinct_id (posthog-core.js ignores get_device_id on the bootstrap path), and the
  // landing-hero flag (200526-no-hero-image) buckets on $device_id. So the id we evaluate the flag
  // with here, the id we bootstrap, and the id the client adopts as $device_id must all be the same
  // value — otherwise SSR and the client resolve different A/B variants for a brand-new visitor.
  // We generate one deviceId and reuse it; returning visitors keep posthog's own persisted ids.
  // A separate device id is only settable via a get_device_id override, which is what broke reset()
  // and caused the user merges — so do NOT reintroduce one.
  let deviceId = crypto.randomUUID()
  let distinctID = deviceId
  let isIdentified = false
  let featureFlags: PosthogBootstrapFlags = {}

  if (posthogToken && (isHomepage || isPricePage || isIntroPage)) {
    const posthogCookie = request.cookies.get(`ph_${posthogToken}_posthog`)
    const forwardedFor = request.headers.get('x-forwarded-for')
    // An IP is a persistent identifier under COPPA, so a student's is never disclosed to PostHog.
    // Geo then resolves from this edge function, so geo-targeted flags stop resolving per-visitor;
    // bucketing is unaffected, keying off distinct_id rather than location.
    const clientIp = appConfig.isSchoolPlatform ? undefined : forwardedFor?.split(',')[0]?.trim()

    if (posthogCookie?.value) {
      try {
        const parsedCookie = JSON.parse(posthogCookie.value) as { distinct_id?: string; $device_id?: string }
        if (parsedCookie.$device_id) {
          deviceId = parsedCookie.$device_id
          distinctID = parsedCookie.$device_id
        }
        if (parsedCookie.distinct_id) {
          distinctID = parsedCookie.distinct_id
          // A numeric distinct_id is a logged-in HOM user id, i.e. an already-identified
          // person. Flag it so posthog does not treat it as anonymous and merge it into the
          // next user identified on this device.
          isIdentified = /^\d+$/.test(parsedCookie.distinct_id)
        }
      } catch {
        // If the cookie value is malformed, keep the generated UUID.
      }
    }

    try {
      const posthogResponse = await fetch(POSTHOG_FLAGS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(clientIp ? { 'X-Forwarded-For': clientIp } : {}),
        },
        body: JSON.stringify({
          api_key: posthogToken,
          distinct_id: distinctID,
          person_properties: { $device_id: deviceId },
          // Scope the bootstrap to flags tagged `bootstrap` in PostHog (configured per-flag
          // under Evaluation contexts). Untagged flags are still returned by PostHog, so every
          // non-bootstrap flag must carry some other context for this filter to be effective.
          evaluation_contexts: ['bootstrap'],
        }),
      })

      const payload = (await posthogResponse.json()) as PosthogFlagsResponse
      featureFlags = normalizeFeatureFlags(payload)
    } catch {
      // Ignore PostHog failures to avoid blocking the response.
    }
  }

  const localePrefix = getLocalePrefix(request.nextUrl.pathname)
  const landingVariant = getLandingVariantFromFlags(featureFlags)
  // Allowlisted, not any ?partner: the value is visitor-supplied and would opt anyone out.
  const partnerKey = request.nextUrl.searchParams.get(CAMPAIGN_PARTNER_PARAM)
  const isPartnerVisit = !!partnerKey && FUNNEL_PARTNER_KEYS.includes(partnerKey)
  const isPartnerFunnelEntry = isIntroPage && isPartnerVisit
  // Partner traffic bypasses the onboarding experiment, in every locale; the pages skip the exposure to match.
  const onboardingArmRoute: OnboardingArmRoute = isPartnerFunnelEntry ? 'control' : getOnboardingArmRoute(featureFlags)
  // Forwarded to instrumentation-client.ts via the bootstrap cookie and applied with
  // posthog.register so they ride the first $pageview. Middleware is the source of truth.
  const superProperties: PosthogSuperProperties = { landing_page_hero_variant: landingVariant }

  const landingRewriteUrl = request.nextUrl.clone()
  landingRewriteUrl.pathname = `${localePrefix}/${landingVariant === 'variant' ? 'landing-variant' : 'landing-control'}`

  const introRewriteUrl = request.nextUrl.clone()
  introRewriteUrl.pathname = `${localePrefix}/get-started/${onboardingArmRoute}`

  const response =
    isHomepage && posthogToken
      ? NextResponse.rewrite(landingRewriteUrl)
      : // Partner entry needs no token: it always gets the control arm.
      (isIntroPage && posthogToken) || isPartnerFunnelEntry
      ? NextResponse.rewrite(introRewriteUrl)
      : NextResponse.next()

  response.cookies.set('__geo_country', country, {
    secure: isHttps,
    sameSite: 'strict',
  })

  response.cookies.set('__geo_city', city, {
    secure: isHttps,
    sameSite: 'strict',
  })

  if (posthogToken && (isHomepage || isPricePage || isIntroPage)) {
    const payload: PosthogBootstrapPayload = { distinctID, isIdentifiedID: isIdentified, featureFlags, superProperties }
    response.cookies.set(POSTHOG_BOOTSTRAP_COOKIE_KEY, JSON.stringify(payload), {
      path: '/',
      secure: isHttps,
      sameSite: 'lax',
      maxAge: 60 * 5,
    })
  }

  return response
}
